import React, { useState, useEffect } from 'react';

// Full organization interface from the API
interface ApiOrganization {
  organization_id: string;
  organization_code: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

// Organization data for form submissions
interface OrganizationFormData {
  organization_id?: string;
  organization_code: string;
  name: string;
  description?: string;
}

interface OrganizationFormProps {
  organization?: ApiOrganization;
  onSubmit: (organizationData: OrganizationFormData) => Promise<void>;
  onCancel: () => void;
}

const OrganizationForm: React.FC<OrganizationFormProps> = ({ 
  organization, 
  onSubmit, 
  onCancel 
}) => {
  const [organizationData, setOrganizationData] = useState<OrganizationFormData>({
    organization_code: '',
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const isEditMode = !!organization?.organization_id;
  
  // If editing, load organization data
  useEffect(() => {
    if (organization) {
      setOrganizationData({
        organization_id: organization.organization_id,
        organization_code: organization.organization_code,
        name: organization.name,
        description: organization.description || ''
      });
    }
  }, [organization]);
  
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setOrganizationData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await onSubmit(organizationData);
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 p-4 rounded-lg text-red-800 mb-4">
          {error}
        </div>
      )}
      
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Organization Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={organizationData.name}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
          required
        />
      </div>
      
      <div>
        <label htmlFor="organization_code" className="block text-sm font-medium text-gray-700 mb-1">
          Organization Code * {isEditMode && '(Cannot be changed)'}
        </label>
        <input
          type="text"
          id="organization_code"
          name="organization_code"
          value={organizationData.organization_code}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
          required
          readOnly={isEditMode}
          disabled={isEditMode}
        />
        {!isEditMode && (
          <p className="mt-1 text-sm text-gray-500">
            This code will be used for login and must be unique
          </p>
        )}
      </div>
      
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={organizationData.description || ''}
          onChange={handleChange}
          rows={3}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
        />
      </div>
      
      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-primary text-secondary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : isEditMode ? 'Update Organization' : 'Create Organization'}
        </button>
      </div>
    </form>
  );
};

export default OrganizationForm;